import Constants from 'expo-constants';
import React, { useEffect, useRef } from 'react';
import { StyleSheet } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import WebView, { WebViewMessageEvent } from 'react-native-webview';

import { type CardData, PaymentProcessor, usePaymentMethodStore } from '..';
import { EncryptionConfig, getRegionalEncryptionConfig } from '../services/webview-encryption-service';

import { buildOrbitalInjectScript, generateOrbitalHtml } from './orbital-encryption-html';

interface EncryptionWebViewProps {
  cardData: CardData;
}

/**
 * EncryptionWebView - Invisible WebView for secure card encryption
 */
// Fallback delay (ms) after the WebView page loads if ENCRYPTION_READY never arrives
const INJECT_FALLBACK_MS = 25_000;
// Short delay after READY before inject (WebView bridge can need a tick to be ready)
const INJECT_AFTER_READY_MS = 100;
// How long the injected Orbital script keeps polling for PIE readiness before failing
const ORBITAL_INJECT_POLL_MS = 10_000;

export const EncryptionWebView = ({ cardData: _cardData }: EncryptionWebViewProps) => {
  const webviewRef = useRef<WebView>(null);
  const encryptionService = usePaymentMethodStore(state => state.encryptionService);
  const injectRef = useRef<() => void>(() => {});
  const fallbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const readyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const didInjectRef = useRef(false);

  const pendingEncryption = encryptionService?.getWebviewPendingEncryption();

  const processor = pendingEncryption?.processor;
  const orderId = pendingEncryption?.orderId || '';
  const webViewSessionKey = pendingEncryption?.webViewSessionKey;
  const config = pendingEncryption?.config;
  const onResult = pendingEncryption?.onResult;
  const onError = pendingEncryption?.onError;

  const digitsOnly = (s: string) => (s ?? '').replace(/\D/g, '');

  const clearFallbackTimer = () => {
    if (fallbackTimeoutRef.current) {
      clearTimeout(fallbackTimeoutRef.current);
      fallbackTimeoutRef.current = null;
    }
  };

  const clearReadyTimer = () => {
    if (readyTimeoutRef.current) {
      clearTimeout(readyTimeoutRef.current);
      readyTimeoutRef.current = null;
    }
  };

  const clearSessionTimers = () => {
    clearFallbackTimer();
    clearReadyTimer();
  };

  const scheduleReadyInject = (sessionKey: string | undefined) => {
    clearReadyTimer();
    readyTimeoutRef.current = setTimeout(() => {
      readyTimeoutRef.current = null;
      const currentSessionKey =
        encryptionService?.getWebviewPendingEncryption()?.webViewSessionKey;
      if (sessionKey !== undefined && currentSessionKey !== sessionKey) return;
      injectRef.current();
    }, INJECT_AFTER_READY_MS);
  };

  const scheduleFallbackTimer = () => {
    clearFallbackTimer();
    fallbackTimeoutRef.current = setTimeout(() => {
      injectRef.current();
    }, INJECT_FALLBACK_MS);
  };

  useEffect(() => {
    const pending = encryptionService?.getWebviewPendingEncryption();
    if (!processor || !config || !pending) return;

    didInjectRef.current = false;
    const sessionKey = pending.webViewSessionKey;
    const cardNumber = digitsOnly(pending.cardData.cardNumber);
    const cvv = digitsOnly(pending.cardData.cvv);

    const doInject = () => {
      const currentSessionKey =
        encryptionService?.getWebviewPendingEncryption()?.webViewSessionKey;
      if (sessionKey !== undefined && currentSessionKey !== sessionKey) return;
      if (didInjectRef.current) return;
      didInjectRef.current = true;
      clearSessionTimers();

      if (processor === PaymentProcessor.ORBITAL) {
        webviewRef.current?.injectJavaScript(
          buildOrbitalInjectScript(
            cardNumber,
            cvv,
            config as NonNullable<EncryptionConfig['orbital']>,
            ORBITAL_INJECT_POLL_MS
          )
        );
      } else {
        webviewRef.current?.injectJavaScript(`
          makeEProtectCall("${cardNumber}", "${cvv}");
          true;
        `);
      }
    };

    injectRef.current = doInject;

    return clearSessionTimers;
  }, [processor, config, orderId, webViewSessionKey, encryptionService]);

  if (!pendingEncryption || !processor || !config || !onResult || !onError) {
    return null;
  }

  const customHtml =
    processor === PaymentProcessor.ORBITAL
      ? generateOrbitalHtml(config as EncryptionConfig['orbital'])
      : generateWorldpayHtml(orderId);

  const orbitalConfig = config as EncryptionConfig['orbital'];
  const webViewSource =
    processor === PaymentProcessor.ORBITAL && orbitalConfig?.baseUrl
      ? { html: customHtml, baseUrl: orbitalConfig.baseUrl }
      : { html: customHtml };

  const messageHandler = (event: WebViewMessageEvent) => {
    try {
      const data = JSON.parse(event.nativeEvent.data);

      if (data?.type === 'ENCRYPTION_READY') {
        clearFallbackTimer();
        scheduleReadyInject(webViewSessionKey);
        return;
      }

      if ('error' in data) {
        onError(data);
      } else {
        if (processor === PaymentProcessor.ORBITAL) {
          onResult({
            ...data,
            subscriberId: config.subscriberId,
            pieFormat: config.pieFormat,
          });
        } else {
          onResult({
            lowValueToken: data.paypageRegistrationId,
            type: data.type,
            bin: data.bin,
            last4: data.lastFour,
          });
        }
      }
    } catch (error) {
      onError({
        error: error instanceof Error ? error.message : 'Failed to parse encryption response',
      });
    }
  };

  return (
    <SafeAreaView style={styles.hidden}>
      <WebView
        key={webViewSessionKey ?? orderId ?? 'orbital-encryption'}
        ref={webviewRef}
        source={webViewSource}
        onMessage={messageHandler}
        onLoadEnd={scheduleFallbackTimer}
        originWhitelist={['*']}
        androidLayerType="software"
        javaScriptEnabled={true}
        domStorageEnabled={true}
        onError={syntheticEvent => {
          const { nativeEvent } = syntheticEvent;
          onError({
            error: `WebView error: ${nativeEvent.description}`,
          });
        }}
        onHttpError={syntheticEvent => {
          const { nativeEvent } = syntheticEvent;
          onError({
            error: `HTTP error: ${nativeEvent.statusCode}`,
          });
        }}
      />
    </SafeAreaView>
  );
};

function generateWorldpayHtml(orderId: string): string {
  const config: EncryptionConfig['worldpay'] = getRegionalEncryptionConfig().worldpay;

  const scriptUrl = `${config?.eProtectUrl}/eProtect/eProtect-api3.js`;
  const merchantOrderId = orderId?.slice(0, 20) || 'N/A';
  const env = Constants.expoConfig?.extra?.eas?.env;
  const buildRegion = Constants.expoConfig?.extra?.eas?.buildRegion;
  const merchantReportGroup = `${env}-FHS-${buildRegion}`.toUpperCase();

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
    </head>
    <body>
      <script src="${scriptUrl}"></script>
      
      <script>
        (function notifyWhenReady() {
          if (typeof eProtect === 'function') {
            setTimeout(function() {
              window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'ENCRYPTION_READY' }));
            }, 0);
          } else {
            setTimeout(notifyWhenReady, 50);
          }
        })();
        function makeEProtectCall(cardNumber, cvv) {
          try {
            if (typeof eProtect !== 'function') {
              throw new Error('eProtect library not loaded');
            }
            
            const preRequest = {
              paypageId: '${config?.paypageId}',
              reportGroup: '${merchantReportGroup}',
              orderId: '${merchantOrderId}',
              id: '${merchantOrderId}',
              url: '${config?.eProtectUrl}'
            };
            
            const formFields = {
              accountNum: { value: cardNumber },
              cvv2: { value: cvv },
            };

            const timeout = 10000;
            
            new eProtect().sendToEprotect(
              preRequest,
              formFields,
              function(data) {
                window.ReactNativeWebView.postMessage(JSON.stringify(data));
              },
              function(error) {
                window.ReactNativeWebView.postMessage(JSON.stringify({
                  error: error.message || 'eProtect error',
                  ...error,
                }));
              },
              function() {
                window.ReactNativeWebView.postMessage(JSON.stringify({
                  error: 'eProtect Request Timeout',
                }));
              },
              timeout
            );
          } catch (err) {
            window.ReactNativeWebView.postMessage(JSON.stringify({
              error: err.message || 'Unknown eProtect error'
            }));
          }
        }
      </script>
    </body>
    </html>
 `;
}

const styles = StyleSheet.create({
  hidden: {
    // Android WebViews with 0x0 dimensions may skip loading external scripts.
    width: 1,
    height: 1,
    opacity: 0,
    position: 'absolute',
  },
});
